import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export default function ProtectedRoute({ children, role, perm }) {
  const { user, bootstrapped } = useAuth();
  const location = useLocation();

  if (!bootstrapped) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass px-6 py-4 fade-up" data-testid="auth-loading">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-blue-500 pulse-soft" />
            <span className="font-mono text-sm text-ink-2">Authenticating…</span>
          </div>
        </div>
      </div>
    );
  }
  // Someone signing out of (or arriving cold at) a partner's own address stays
  // on that partner's site — a client should never see our operator login.
  if (!user) {
    const onPartnerSite = location.pathname.match(/^\/p\/([^/]+)\//);
    const to = onPartnerSite ? `/p/${onPartnerSite[1]}/login` : "/login";
    return <Navigate to={to} state={{ from: location }} replace />;
  }
  // A partner's own client only ever sees their statement — and they see it on
  // their adviser's own address (/p/:slug/portal), never inside our app.
  if (user.role === "partner_client") {
    const ok = location.pathname.startsWith("/client") || location.pathname.startsWith("/p/");
    return ok ? children : <Navigate to="/client" replace />;
  }
  if (location.pathname.startsWith("/client")) return <Navigate to="/dashboard" replace />;
  // Two ways into the partner console. A standalone partner login only ever sees the
  // console. A BotChainCP member who has been made a partner gets the console AND
  // keeps their member dashboard — same account, same credentials, switchable.
  const partnerOnly = user.role === "partner";
  const canPartner = partnerOnly || (!!user.partner_access && !!user.partner_id);
  if (partnerOnly && !location.pathname.startsWith("/partner")) return <Navigate to="/partner" replace />;
  if (!canPartner && location.pathname.startsWith("/partner")) return <Navigate to="/dashboard" replace />;
  // Self-registered accounts are frozen until their sponsor / an admin approves
  // them — they can only see the pending screen (and the public landing page).
  if (user.pending_approval && location.pathname !== "/pending-approval") {
    return <Navigate to="/pending-approval" replace />;
  }
  // Onboarding gate: locked new members can only see the Getting Started page
  // plus its training material (Smart Setup + the 12-step manual).
  const lockedAllowed = ["/getting-started", "/setup"];
  if (user.onboarding_locked && !lockedAllowed.includes(location.pathname)) {
    return <Navigate to="/getting-started" replace />;
  }
  if (role) {
    const allowed = Array.isArray(role) ? role : [role];
    // A marketing manager gets the Share links page for their campaign builder,
    // even though they aren't an admin.
    const marketingPass = perm === "marketing" && user.marketing_access;
    // The demo mirrors an admin for data scope, but it never gets the consoles.
    if (user.is_demo || (!allowed.includes(user.role) && !marketingPass)) return <Navigate to="/dashboard" replace />;
  }
  // Opt-in capabilities the super admin grants per member (Operation Momentum).
  if (perm === "momentum" && (!user.momentum_access || user.is_demo)) return <Navigate to="/dashboard" replace />;
  // Super-admin-only consoles (the partner programme).
  if (perm === "super" && (!user.is_super_admin || user.is_demo)) return <Navigate to="/dashboard" replace />;
  return children;
}
